import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check } from "./Icon";

/**
 * Toast — composes `@norkron/web-toast`. Used for lightweight copy-feedback
 * ("link copied", "hash copied") so the reader stays in flow (design principle
 * 2 — no modal-per-action).
 */

interface ToastCtx {
  show: (message: string) => void;
}
const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);

  const show = useCallback((msg: string) => {
    setMessage(msg);
    window.setTimeout(() => setMessage(null), 2200);
  }, []);

  return (
    <Ctx.Provider value={{ show }}>
      {children}
      {message
        ? createPortal(
            <div className="toast" role="status" data-testid="toast">
              <Check size={15} strokeWidth={2} />
              {message}
            </div>,
            document.body,
          )
        : null}
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}
